import type { EventSnapshot } from '../types'
import { saoPauloParts } from './schedule'

// What the app's card and event page say about an event (docs/cms-hub.md):
// the day and time of its one session, or how many dates or times, or
// Encerrado; the place, or how many; the price. The app's rules
// (utils/scheduleCounts.ts, utils/dateUtils.ts), kept small enough to stay
// the same.

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
const SESSION_GRACE_MS = 15 * 60_000

const count = (value: number, noun: string) => `${value > 9 ? '9+' : value} ${noun}`

export function previewDay(iso: string) {
  const { date, weekday } = saoPauloParts(iso)
  const [, month, day] = date.split('-').map(Number)
  return `${WEEKDAYS[weekday]}, ${day} de ${MONTHS[month - 1]}`
}

const money = (value: number) => value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function eventPreview(snapshot: EventSnapshot, now = Date.now()) {
  const runs = snapshot.runs.filter((run) => run.status !== 'deleted')
  const sessions = runs
    .filter((run) => run.status !== 'canceled')
    .flatMap((run) => run.occurrences.filter((item) => item.status === 'published' && item.starts_at))
    .filter((item) => new Date(item.ends_at ?? item.starts_at).getTime() + (item.ends_at ? 0 : SESSION_GRACE_MS) > now)
    .sort((left, right) => left.starts_at.localeCompare(right.starts_at))

  let when = 'Encerrado'
  if (sessions.length === 1) {
    const [only] = sessions
    const start = saoPauloParts(only.starts_at).time
    const end = only.ends_at ? saoPauloParts(only.ends_at).time : null
    when = `${previewDay(only.starts_at)} · ${end ? `${start} – ${end}` : start}`
  } else if (sessions.length > 1) {
    const dates = new Set(sessions.map((item) => saoPauloParts(item.starts_at).date))
    when = dates.size > 1 ? count(dates.size, 'datas') : count(sessions.length, 'horários')
  }

  const places = [...new Set(runs.map((run) => run.place_name.trim()).filter(Boolean))]
  const place = places.length > 1 ? count(places.length, 'locais') : places[0] || 'Local a definir'

  const labels = runs.map((run) => run.price_label.trim()).filter(Boolean)
  const prices = runs.filter((run) => !run.is_free && run.price_min !== null).map((run) => run.price_min as number)
  const price = labels.length === runs.length && labels.length ? labels[0]
    : runs.length && runs.every((run) => run.is_free) ? 'Gratuito'
      : prices.length ? `A partir de R$ ${money(Math.min(...prices))}`
        : 'Preço não informado'

  return { when, place, price }
}
